// Terms & Conditions translations — English / Hindi / Bengali
// Kept in-file (not in a locale-service) because these are legal strings and
// they are used only on the T&C gate page.
//
// Convention:
//   Any occurrence of **word** inside a `body` string will be rendered as <strong>word</strong>
//   in the UI. See `renderBold()` helper in PublicFormPage's TermsGate.

export const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "bn", label: "বাংলা" },
];

const en = {
  step_label: "Step 1 of 2 — Accept Terms",
  h1: "Read & Accept the Terms",
  subheading:
    "To make sure only genuine job seekers register, please read the Terms & Conditions in full and tap I AGREE to unlock the application form.",
  doc_title: "Tulsi Jobs Placement Consultant — Terms & Conditions",
  intro:
    "By submitting your details on this platform and accepting any placement offered by Tulsi Jobs Placement Consultant, you acknowledge and agree to the following terms.",
  sections: [
    {
      n: "1.",
      title: "Application & Use of Personal Data",
      body: "You voluntarily submit your personal and professional details (name, contact, qualifications, work history, CV, photograph, etc.) to Tulsi Jobs for the purpose of matching you with suitable employment opportunities. We may share your profile with prospective employers, partner companies, and our internal recruiters in furtherance of this objective.",
    },
    {
      n: "2.",
      title: "Employment Agreement (applies on placement)",
      body: "The following clauses constitute the standard employment agreement between Tulsi Jobs and any candidate placed via our consultancy. By accepting a job offered through Tulsi Jobs you agree to be bound by these clauses with the placing employer:",
    },
  ],
  subs: [
    {
      title: "2.1 Appointment",
      body: "Tulsi Jobs has facilitated the placement of the Candidate with the placing company (\"Employer\") for the role offered. The Candidate agrees to join the Employer in the offered position with effect from the joining date communicated in the offer letter.",
    },
    {
      title: "2.2 Salary Structure",
      body: "The monthly salary agreed upon for the position shall be as stated in the offer letter. During the first month (probationary month), the Candidate shall receive **70% of the agreed monthly salary**. The remaining **30%** of the first month's salary shall be deducted and retained as a **consultancy / probation adjustment** by Tulsi Jobs. From the second month of employment onwards, the Candidate shall receive **100% of the agreed monthly salary** directly from the Employer.",
    },
    {
      title: "2.3 Probation Period",
      body: "The first month of employment shall be treated as a probation period. Continuation of employment beyond the probation period is subject to (a) satisfactory performance as judged by the Employer and (b) adherence to the Employer's company policies. Tulsi Jobs is not responsible for the continuation decision; that decision rests solely with the Employer.",
    },
  ],
  sections_after: [
    {
      n: "3.",
      title: "Honest Information",
      body: "You confirm that the information submitted is true, accurate and not misleading. Misrepresentation may lead to rejection, blacklisting, or termination by the Employer with no obligation on Tulsi Jobs to refund or compensate.",
    },
    {
      n: "4.",
      title: "Communication consent",
      body: "We may contact you via phone, SMS, WhatsApp, or email regarding opportunities and your application. You consent to receive such communications.",
    },
    {
      n: "5.",
      title: "Jurisdiction",
      body: "These terms shall be governed by the laws of India.",
    },
  ],
  full_version_note: "Full version available at",
  agree_label:
    "**I AGREE** — I have read and understood the Terms & Conditions, including the salary & probation clauses 2.2 and 2.3 applicable on placement.",
  scroll_hint: "↓ Please scroll through the entire terms to enable this checkbox.",
  accept_button: "I AGREE — Continue to Application Form",
  timestamp_note:
    "Your acceptance is timestamped and a signed acknowledgement PDF is attached to your application automatically.",
  choose_language: "Choose your language",
};

const hi = {
  step_label: "चरण 1 का 2 — शर्तें स्वीकार करें",
  h1: "नियम और शर्तें पढ़ें और स्वीकार करें",
  subheading:
    "यह सुनिश्चित करने के लिए कि केवल वास्तविक नौकरी चाहने वाले ही पंजीकरण करें, कृपया नीचे दिए गए नियम एवं शर्तों को पूरा पढ़ें और आवेदन फ़ॉर्म खोलने के लिए मैं सहमत हूँ पर टैप करें।",
  doc_title: "तुलसी जॉब्स प्लेसमेंट कंसल्टेंट — नियम एवं शर्तें",
  intro:
    "इस प्लेटफ़ॉर्म पर अपना विवरण जमा करके और तुलसी जॉब्स प्लेसमेंट कंसल्टेंट द्वारा प्रस्तावित किसी भी नियुक्ति को स्वीकार करके, आप निम्नलिखित शर्तों को स्वीकार करते हैं और सहमति देते हैं।",
  sections: [
    {
      n: "1.",
      title: "आवेदन एवं व्यक्तिगत जानकारी का उपयोग",
      body: "आप स्वेच्छा से अपनी व्यक्तिगत और व्यावसायिक जानकारी (नाम, संपर्क, योग्यता, कार्य इतिहास, सीवी, फोटो आदि) उपयुक्त रोज़गार अवसरों से मिलान के उद्देश्य से तुलसी जॉब्स को प्रदान करते हैं। इस उद्देश्य को पूरा करने के लिए हम आपकी प्रोफ़ाइल संभावित नियोक्ताओं, साझेदार कंपनियों और हमारे आंतरिक भर्तीकर्ताओं के साथ साझा कर सकते हैं।",
    },
    {
      n: "2.",
      title: "नियुक्ति समझौता (नियुक्ति पर लागू)",
      body: "निम्नलिखित शर्तें तुलसी जॉब्स और हमारी कंसल्टेंसी के माध्यम से नियुक्त किसी भी उम्मीदवार के बीच मानक रोज़गार समझौते का हिस्सा हैं। तुलसी जॉब्स के माध्यम से मिली नौकरी स्वीकार करने पर आप संबंधित नियोक्ता के साथ इन शर्तों से बंधे होने के लिए सहमत होते हैं:",
    },
  ],
  subs: [
    {
      title: "2.1 नियुक्ति",
      body: "तुलसी जॉब्स ने उम्मीदवार की नियुक्ति संबंधित कंपनी (\"नियोक्ता\") में प्रस्तावित पद के लिए कराई है। उम्मीदवार ऑफ़र लेटर में बताई गई जॉइनिंग तिथि से नियोक्ता के साथ प्रस्तावित पद पर जुड़ने के लिए सहमत होता है।",
    },
    {
      title: "2.2 वेतन संरचना",
      body: "पद के लिए तय मासिक वेतन ऑफ़र लेटर में दिए अनुसार होगा। पहले महीने (परिवीक्षा माह) के दौरान उम्मीदवार को **तय मासिक वेतन का 70%** प्राप्त होगा। पहले महीने के वेतन का शेष **30%** तुलसी जॉब्स द्वारा **कंसल्टेंसी / परिवीक्षा समायोजन** के रूप में काटा और रखा जाएगा। रोज़गार के दूसरे महीने से उम्मीदवार को सीधे नियोक्ता से **तय मासिक वेतन का 100%** प्राप्त होगा।",
    },
    {
      title: "2.3 परिवीक्षा अवधि",
      body: "रोज़गार का पहला महीना परिवीक्षा अवधि माना जाएगा। परिवीक्षा अवधि के बाद रोज़गार जारी रहना (क) नियोक्ता द्वारा संतोषजनक प्रदर्शन और (ख) नियोक्ता की कंपनी नीतियों के पालन पर निर्भर है। इस निर्णय के लिए तुलसी जॉब्स ज़िम्मेदार नहीं है; यह निर्णय पूर्णतः नियोक्ता का होगा।",
    },
  ],
  sections_after: [
    {
      n: "3.",
      title: "सही जानकारी",
      body: "आप पुष्टि करते हैं कि दी गई जानकारी सत्य, सटीक और भ्रामक नहीं है। ग़लत जानकारी देने पर नियोक्ता द्वारा अस्वीकृति, ब्लैकलिस्टिंग या समाप्ति की जा सकती है, और तुलसी जॉब्स का किसी प्रकार का रिफ़ंड या मुआवज़ा देने का कोई दायित्व नहीं होगा।",
    },
    {
      n: "4.",
      title: "संपर्क करने की सहमति",
      body: "हम अवसरों और आपके आवेदन के संबंध में आपसे फ़ोन, SMS, WhatsApp या ईमेल द्वारा संपर्क कर सकते हैं। आप ऐसे संचार प्राप्त करने के लिए सहमति देते हैं।",
    },
    {
      n: "5.",
      title: "अधिकार क्षेत्र",
      body: "ये शर्तें भारत के क़ानूनों द्वारा शासित होंगी।",
    },
  ],
  full_version_note: "पूरा दस्तावेज़ यहाँ उपलब्ध है",
  agree_label:
    "**मैं सहमत हूँ** — मैंने नियम एवं शर्तें, विशेषकर नियुक्ति पर लागू वेतन एवं परिवीक्षा खंड 2.2 और 2.3 को पढ़ लिया है और समझ लिया है।",
  scroll_hint: "↓ इस चेकबॉक्स को सक्षम करने के लिए कृपया पूरी शर्तें स्क्रॉल करें।",
  accept_button: "मैं सहमत हूँ — आवेदन फ़ॉर्म पर जाएँ",
  timestamp_note:
    "आपकी स्वीकृति की समय-मुद्रा दर्ज होती है और एक हस्ताक्षरित पुष्टि PDF आपके आवेदन के साथ स्वतः संलग्न की जाती है।",
  choose_language: "अपनी भाषा चुनें",
};

const bn = {
  step_label: "ধাপ ১-এর ২ — শর্তাবলী স্বীকার করুন",
  h1: "শর্তাবলী পড়ুন ও সম্মত হন",
  subheading:
    "শুধুমাত্র প্রকৃত চাকরিপ্রার্থীরাই যাতে নিবন্ধন করেন তা নিশ্চিত করতে, দয়া করে সম্পূর্ণ শর্তাবলী পড়ুন এবং আবেদন ফর্ম খুলতে আমি সম্মত-এ ট্যাপ করুন।",
  doc_title: "তুলসী জবস প্লেসমেন্ট কনসালট্যান্ট — শর্তাবলী",
  intro:
    "এই প্ল্যাটফর্মে আপনার তথ্য জমা দিয়ে এবং তুলসী জবস প্লেসমেন্ট কনসালট্যান্ট কর্তৃক প্রদত্ত যে কোনও নিয়োগ গ্রহণ করে, আপনি নিম্নলিখিত শর্তগুলি স্বীকার করছেন এবং সম্মত হচ্ছেন।",
  sections: [
    {
      n: "১.",
      title: "আবেদন ও ব্যক্তিগত তথ্যের ব্যবহার",
      body: "আপনি স্বেচ্ছায় আপনার ব্যক্তিগত ও পেশাগত বিবরণ (নাম, যোগাযোগ, যোগ্যতা, কর্ম ইতিহাস, সিভি, ছবি ইত্যাদি) উপযুক্ত কর্মসংস্থানের সঙ্গে মেলানোর উদ্দেশ্যে তুলসী জবসকে প্রদান করেন। এই উদ্দেশ্য বাস্তবায়নে আমরা আপনার প্রোফাইল সম্ভাব্য নিয়োগকর্তা, অংশীদার সংস্থা এবং আমাদের অভ্যন্তরীণ নিয়োগকারীদের সঙ্গে শেয়ার করতে পারি।",
    },
    {
      n: "২.",
      title: "নিয়োগ চুক্তি (নিয়োগে প্রযোজ্য)",
      body: "নিম্নলিখিত ধারাগুলি তুলসী জবস এবং আমাদের কনসালট্যান্সির মাধ্যমে নিযুক্ত যে কোনও প্রার্থীর মধ্যে মানক নিয়োগ চুক্তি গঠন করে। তুলসী জবসের মাধ্যমে দেওয়া কোনও চাকরি গ্রহণ করলে আপনি সংশ্লিষ্ট নিয়োগকর্তার সঙ্গে এই ধারাগুলিতে আবদ্ধ থাকতে সম্মত হন:",
    },
  ],
  subs: [
    {
      title: "২.১ নিয়োগ",
      body: "তুলসী জবস প্রার্থীর নিয়োগ সংশ্লিষ্ট সংস্থায় (\"নিয়োগকর্তা\") প্রস্তাবিত পদের জন্য সুবিধা করে দিয়েছে। প্রার্থী অফার লেটারে জানানো যোগদান তারিখ থেকে নিয়োগকর্তার সঙ্গে প্রস্তাবিত পদে যোগ দিতে সম্মত হন।",
    },
    {
      title: "২.২ বেতন কাঠামো",
      body: "পদের জন্য সম্মত মাসিক বেতন অফার লেটারে উল্লেখ অনুযায়ী হবে। প্রথম মাসে (প্রবেশন মাস) প্রার্থী **সম্মত মাসিক বেতনের ৭০%** পাবেন। প্রথম মাসের বেতনের বাকি **৩০%** তুলসী জবস কর্তৃক **কনসালট্যান্সি / প্রবেশন সামঞ্জস্য** হিসাবে কাটা এবং সংরক্ষিত হবে। চাকরির দ্বিতীয় মাস থেকে প্রার্থী সরাসরি নিয়োগকর্তার কাছ থেকে **সম্মত মাসিক বেতনের ১০০%** পাবেন।",
    },
    {
      title: "২.৩ প্রবেশন সময়কাল",
      body: "চাকরির প্রথম মাসকে প্রবেশন সময়কাল হিসাবে গণ্য করা হবে। প্রবেশনের পরে চাকরি চালিয়ে যাওয়া (ক) নিয়োগকর্তার বিচারে সন্তোষজনক পারফরম্যান্স এবং (খ) নিয়োগকর্তার সংস্থার নীতিমালা অনুসরণের উপর নির্ভর করে। এই সিদ্ধান্তের জন্য তুলসী জবস দায়ী নয়; এই সিদ্ধান্ত সম্পূর্ণরূপে নিয়োগকর্তার।",
    },
  ],
  sections_after: [
    {
      n: "৩.",
      title: "সঠিক তথ্য",
      body: "আপনি নিশ্চিত করছেন যে জমা দেওয়া তথ্য সত্য, সঠিক এবং বিভ্রান্তিকর নয়। ভুল তথ্য দিলে নিয়োগকর্তা কর্তৃক প্রত্যাখ্যান, ব্ল্যাকলিস্টিং বা চাকরি থেকে অব্যাহতি হতে পারে এবং তুলসী জবসের কোনও ফেরত বা ক্ষতিপূরণ দেওয়ার বাধ্যবাধকতা থাকবে না।",
    },
    {
      n: "৪.",
      title: "যোগাযোগে সম্মতি",
      body: "আমরা সুযোগ ও আপনার আবেদন সম্পর্কে ফোন, SMS, WhatsApp বা ইমেলের মাধ্যমে আপনার সঙ্গে যোগাযোগ করতে পারি। আপনি এই ধরনের যোগাযোগ পেতে সম্মতি দিচ্ছেন।",
    },
    {
      n: "৫.",
      title: "আইনগত এলাকা",
      body: "এই শর্তাবলী ভারতের আইনে পরিচালিত হবে।",
    },
  ],
  full_version_note: "সম্পূর্ণ সংস্করণ এখানে পাওয়া যায়",
  agree_label:
    "**আমি সম্মত** — আমি শর্তাবলী পড়েছি এবং বুঝেছি, বিশেষত নিয়োগে প্রযোজ্য বেতন ও প্রবেশন সংক্রান্ত ধারা ২.২ ও ২.৩।",
  scroll_hint: "↓ এই চেকবক্সটি সক্রিয় করতে অনুগ্রহ করে সম্পূর্ণ শর্তাবলী স্ক্রল করুন।",
  accept_button: "আমি সম্মত — আবেদন ফর্মে যান",
  timestamp_note:
    "আপনার সম্মতি টাইমস্ট্যাম্প করা হয় এবং একটি স্বাক্ষরিত স্বীকৃতিপত্র PDF স্বয়ংক্রিয়ভাবে আপনার আবেদনের সঙ্গে সংযুক্ত হয়।",
  choose_language: "আপনার ভাষা বেছে নিন",
};

const DICT = { en, hi, bn };

export function tTerms(lang) {
  return DICT[lang] || DICT.en;
}
